import { motion, AnimatePresence } from 'framer-motion'
import { CheckCircle2, X, Send } from 'lucide-react'
import { Button } from '@/components/ui/Button'

type RevisaoForm = { nome: string; email: string; telefone: string; mensagem: string }

type Props = {
  open: boolean
  revisaoForm: RevisaoForm
  revisaoLoading: boolean
  revisaoEnviada: boolean
  onClose: () => void
  onResetEnviada: () => void
  onChange: (patch: Partial<RevisaoForm>) => void
  onSubmit: () => void
}

export function PropostaPublicaRevisaoModal({
  open,
  revisaoForm,
  revisaoLoading,
  revisaoEnviada,
  onClose,
  onResetEnviada,
  onChange,
  onSubmit,
}: Props) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          onClick={() => !revisaoLoading && onClose()}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="proposta-publica-page bg-[var(--bg-card)] border border-[var(--glass-border)] rounded-xl max-w-md w-full p-6 space-y-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {revisaoEnviada ? (
              <div className="text-center space-y-3 py-4">
                <CheckCircle2 size={40} className="mx-auto text-[var(--brand-primary)]" />
                <h3 className="text-lg font-bold text-[var(--text-primary)]">Solicitação Enviada!</h3>
                <p className="text-sm text-[var(--text-muted)]">
                  Sua solicitação de revisão foi enviada. Nossa equipe entrará em contato em breve.
                </p>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    onClose()
                    onResetEnviada()
                  }}
                >
                  Fechar
                </Button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-bold text-[var(--text-primary)]">Solicitar Revisão</h3>
                  <button
                    type="button"
                    onClick={onClose}
                    className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                    disabled={revisaoLoading}
                  >
                    <X size={18} />
                  </button>
                </div>
                <p className="text-xs text-[var(--text-muted)]">
                  Preencha seus dados e descreva as alterações necessárias na proposta.
                </p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-[var(--text-muted)] mb-1">Nome *</label>
                    <input
                      value={revisaoForm.nome}
                      onChange={(e) => onChange({ nome: e.target.value })}
                      placeholder="Seu nome"
                      className="w-full bg-[var(--glass-bg)] border border-[var(--glass-border)] text-[var(--text-primary)] text-sm rounded-md px-3 py-2 outline-none focus:border-[var(--brand-primary)] transition-colors"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-[var(--text-muted)] mb-1">Email *</label>
                      <input
                        type="email"
                        value={revisaoForm.email}
                        onChange={(e) => onChange({ email: e.target.value })}
                        placeholder="seu@email.com"
                        className="w-full bg-[var(--glass-bg)] border border-[var(--glass-border)] text-[var(--text-primary)] text-sm rounded-md px-3 py-2 outline-none focus:border-[var(--brand-primary)] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-[var(--text-muted)] mb-1">Telefone</label>
                      <input
                        value={revisaoForm.telefone}
                        onChange={(e) => onChange({ telefone: e.target.value })}
                        placeholder="(11) 99999-9999"
                        className="w-full bg-[var(--glass-bg)] border border-[var(--glass-border)] text-[var(--text-primary)] text-sm rounded-md px-3 py-2 outline-none focus:border-[var(--brand-primary)] transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-[var(--text-muted)] mb-1">Mensagem *</label>
                    <textarea
                      value={revisaoForm.mensagem}
                      onChange={(e) => onChange({ mensagem: e.target.value })}
                      placeholder="Descreva as alterações necessárias..."
                      rows={4}
                      maxLength={2000}
                      className="w-full bg-[var(--glass-bg)] border border-[var(--glass-border)] text-[var(--text-primary)] text-sm rounded-md px-3 py-2 outline-none focus:border-[var(--brand-primary)] transition-colors resize-none"
                    />
                    <p className="text-[10px] text-[var(--text-muted)] mt-1 text-right">
                      {revisaoForm.mensagem.length}/2000
                    </p>
                  </div>
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  className="w-full"
                  icon={<Send size={14} />}
                  loading={revisaoLoading}
                  disabled={
                    !revisaoForm.nome.trim() ||
                    !revisaoForm.email.trim() ||
                    revisaoForm.mensagem.length < 5
                  }
                  onClick={onSubmit}
                >
                  Enviar Solicitação
                </Button>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
